import React, { type ReactElement, type ReactNode } from "react"

export type CellEditors = Record<string, ReactElement>

function cellColumnKey(child: ReactNode): string | null {
  if (!React.isValidElement(child) || child.key == null) return null
  return String(child.key)
}

// Rebuilds an inline-edit row from the cells the table actually rendered,
// swapping each column's read-only cell for its editor by column key. Matching
// on key rather than index keeps the row aligned under any column config —
// hidden, reordered, resized — and under permission-gated (`hidden`) columns,
// which never reach `children` at all.
//
// `columnKeys` are the configurable columns. Cells outside that set are either
// structural columns the table prepends (a selection checkbox, a drag grip) or
// the trailing action column. rc-table keys keyless columns positionally, so
// they cannot be told apart by key — position is the only signal: anything
// before the first configurable cell is prepended and passes through untouched,
// anything after is dropped in favour of `actionsCell`. That cell is always
// appended, because an edit row needs its save/cancel controls even on tables
// where the action column itself is hidden.
export function buildEditRowCells(
  children: ReactNode,
  columnKeys: readonly string[],
  editors: CellEditors,
  actionsCell: ReactElement
): ReactNode[] {
  const configurable = new Set<string>(columnKeys)
  const cells: ReactNode[] = []
  let seenConfigurable = false

  React.Children.forEach(children, (child) => {
    const key = cellColumnKey(child)
    if (key == null) return

    if (!configurable.has(key)) {
      if (!seenConfigurable) cells.push(child)
      return
    }

    seenConfigurable = true
    const editor = editors[key]
    cells.push(editor ? React.cloneElement(editor, { key }) : child)
  })

  cells.push(React.cloneElement(actionsCell, { key: "actions" }))
  return cells
}

// Decides whether a keydown inside an inline-edit row should commit the row,
// the same way clicking the save (check) control does. Enter commits unless
// it belongs to a control where Enter already has its own meaning:
// - an AntD Select (wrapped in `.ant-select`), where Enter picks an option when
//   the dropdown is open and opens it when closed. Matching on the wrapper
//   rather than `aria-expanded` is deliberate: rc-select opens on Enter with
//   `preventDefault()` but no `stopPropagation()`, and renders `aria-expanded`
//   from the current render — so a *closed* Select still reads `"false"` when
//   the keydown reaches the row, and one keystroke would open the dropdown and
//   commit the row at the same time;
// - a <button>, which already turns its own Enter keydown into a click —
//   letting that fire natively (rather than also committing here) keeps a
//   focused Cancel button cancelling, not saving, and avoids the save button
//   firing the save handler twice.
// - an AntD DatePicker input (wrapped in `.ant-picker`), where Enter confirms
//   the date, which would otherwise also commit the row on the same keystroke.
//
// A <textarea> is NOT excluded. Several edit rows render their main text cell
// as `Input.TextArea autoSize` purely so long values wrap in a narrow column,
// so excluding it would leave Enter dead in the very field the user is most
// likely typing in. Shift+Enter is the newline there instead — and, being the
// newline modifier, never commits from any control.
//
// Auto-repeat never commits: only the first keydown of a held Enter counts.
export function isRowCommitEnter(
  event: Pick<React.KeyboardEvent, "key" | "target"> & {
    repeat?: boolean
    shiftKey?: boolean
  }
): boolean {
  if (event.key !== "Enter") return false
  // Holding Enter auto-repeats; without this a held key stacks identical
  // save requests, one per repeat.
  if (event.repeat) return false
  if (event.shiftKey) return false

  const target = event.target as HTMLElement
  const tag = target?.tagName
  if (tag === "BUTTON") return false
  if (target?.closest?.(".ant-select")) return false
  if (target?.closest?.(".ant-picker")) return false

  return true
}

// Escape cancels the row the way its ✕ does, unless it is closing a popup the
// focused control has open (a Select's list, a DatePicker's calendar): that
// Escape belongs to the popup.
export function isRowCancelEscape(event: {
  key: string
  target: EventTarget | null
  repeat?: boolean
}): boolean {
  if (event.key !== "Escape" || event.repeat) return false

  const target = event.target as HTMLElement
  if (target?.getAttribute?.("aria-expanded") === "true") return false
  if (
    target?.closest?.(".ant-picker") &&
    document.querySelector(
      ".ant-picker-dropdown:not(.ant-picker-dropdown-hidden)"
    )
  )
    return false

  return true
}

interface RowCommitKeyDownOptions {
  /** A handler arriving via the table's `onRow`, which must still run. */
  incoming?: React.KeyboardEventHandler<HTMLTableRowElement>
  /** Suppresses the commit while a save is already in flight. */
  busy?: boolean
  /** What Escape does in the row, usually the row's own ✕. */
  cancel?: () => void
}

/**
 * Builds the `onKeyDown` for an inline-edit row.
 *
 * Shared so the edit rows cannot drift apart on the parts that are easy to get
 * wrong: forwarding a handler that arrived via `onRow` rather than replacing
 * it, and refusing to commit while a save is in flight.
 */
export function rowCommitKeyDown(
  commit: () => void,
  { incoming, busy, cancel }: RowCommitKeyDownOptions = {}
): React.KeyboardEventHandler<HTMLTableRowElement> {
  return (event) => {
    incoming?.(event)
    if (busy) return
    if (cancel && isRowCancelEscape(event)) {
      event.preventDefault()
      cancel()
      return
    }
    if (!isRowCommitEnter(event)) return
    // The row is taking this Enter, so stop the control acting on it too: a
    // textarea would take a newline, and an ancestor <form> a submit.
    event.preventDefault()
    commit()
  }
}
